import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Globe,
  Send,
  Clock,
  Save,
  Eye,
  History,
  RotateCcw,
  ExternalLink,
  Lock,
  ChevronLeft,
  Columns2,
  Settings2,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Textarea } from '../components/ui/Input.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { cn } from '../components/ui/cn.ts';
import { MediaPickerModal } from '../components/ui/MediaPickerModal.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface BlockDef {
  id: string;
  name: string;
  slug: string;
  category: string;
  fieldsJson: {
    key: string;
    label: string;
    type: string;
    required?: boolean;
    options?: string[];
    collectionKey?: string;
  }[];
  isActive: boolean;
}

type BuiltinBlockType =
  | 'hero'
  | 'cta'
  | 'rich_text'
  | 'text'
  | 'image'
  | 'banner'
  | 'card_grid'
  | 'feature_grid'
  | 'testimonial'
  | 'pricing_table'
  | 'form'
  | 'spacer'
  | 'html'
  | 'loyalty_points'
  | 'loyalty_rewards'
  | 'loyalty_tier'
  | 'video'
  | 'accordion'
  | 'countdown'
  | 'row';

type BlockType = BuiltinBlockType | string;

interface CmsBlock {
  id: string;
  blockDefinitionId: string | null;
  sortOrder: number;
  props: Record<string, unknown>;
  loyaltyMembersOnly?: boolean;
  parentBlockId?: string | null;
}

// ─── Row layout presets ────────────────────────────────────────────────────────

const COLUMN_PRESETS: Record<number, Array<{ label: string; widths: string[] }>> = {
  1: [{ label: 'Full width', widths: ['full'] }],
  2: [
    { label: '½ + ½', widths: ['1/2', '1/2'] },
    { label: '⅓ + ⅔', widths: ['1/3', '2/3'] },
    { label: '⅔ + ⅓', widths: ['2/3', '1/3'] },
    { label: '¼ + ¾', widths: ['1/4', '3/4'] },
    { label: '¾ + ¼', widths: ['3/4', '1/4'] },
  ],
  3: [
    { label: '⅓ + ⅓ + ⅓', widths: ['1/3', '1/3', '1/3'] },
    { label: '¼ + ½ + ¼', widths: ['1/4', '1/2', '1/4'] },
  ],
  4: [{ label: '¼ × 4', widths: ['1/4', '1/4', '1/4', '1/4'] }],
};

interface CardItem {
  title: string;
  description: string;
  imageUrl: string;
  link: string;
}

interface CmsPage {
  id: string;
  slug: string;
  title: string;
  status: 'draft' | 'published';
  isPublic: boolean;
  isTemplate: boolean;
  loyaltyMembersOnly: boolean;
  layoutId: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  blocks: CmsBlock[];
}

interface CmsLayout {
  id: string;
  name: string;
  slug: string;
  isDefault: boolean;
}

interface Version {
  id: string;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDateTime(d: string) {
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Block props editor for built-in block types ─────────────────────────────

function BuiltinBlockEditor({
  slug,
  props,
  onChange,
}: {
  slug: string;
  props: Record<string, unknown>;
  onChange: (props: Record<string, unknown>) => void;
}) {
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState<string>('url');

  function set(key: string, value: unknown) {
    onChange({ ...props, [key]: value });
  }

  const labelCls = 'block text-xs font-medium text-zinc-600 mb-1';
  const inputCls =
    'w-full border border-zinc-200 rounded-lg text-sm px-3 h-8 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white';
  const rowCls = 'grid grid-cols-2 gap-3';

  switch (slug) {
    case 'hero':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Subtitle</label>
            <input
              className={inputCls}
              value={String(props.subtitle ?? '')}
              onChange={(e) => set('subtitle', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Background URL</label>
            <input
              className={inputCls}
              value={String(props.backgroundUrl ?? '')}
              onChange={(e) => set('backgroundUrl', e.target.value)}
              placeholder="https://…"
            />
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Text alignment</label>
              <select
                className={inputCls}
                value={String(props.textAlign ?? 'center')}
                onChange={(e) => set('textAlign', e.target.value)}
              >
                {['left', 'center', 'right'].map((v) => (
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Overlay color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={String(props.overlayColor ?? '#000000')}
                  onChange={(e) => set('overlayColor', e.target.value)}
                  className="h-8 w-10 rounded border border-zinc-200 cursor-pointer p-0.5 flex-shrink-0"
                />
                <input
                  className={cn(inputCls, 'flex-1 font-mono')}
                  value={String(props.overlayColor ?? '')}
                  onChange={(e) => set('overlayColor', e.target.value)}
                  placeholder="rgba(0,0,0,0.4)"
                />
              </div>
            </div>
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>CTA Text</label>
              <input
                className={inputCls}
                value={String(props.ctaText ?? '')}
                onChange={(e) => set('ctaText', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>CTA Link</label>
              <input
                className={inputCls}
                value={String(props.ctaLink ?? '')}
                onChange={(e) => set('ctaLink', e.target.value)}
                placeholder="/page or https://…"
              />
            </div>
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>CTA style</label>
              <select
                className={inputCls}
                value={String(props.ctaVariant ?? 'primary')}
                onChange={(e) => set('ctaVariant', e.target.value)}
              >
                {['primary', 'secondary', 'outline', 'ghost'].map((v) => (
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Secondary CTA text</label>
              <input
                className={inputCls}
                value={String(props.secondaryCtaText ?? '')}
                onChange={(e) => set('secondaryCtaText', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Secondary CTA link</label>
            <input
              className={inputCls}
              value={String(props.secondaryCtaLink ?? '')}
              onChange={(e) => set('secondaryCtaLink', e.target.value)}
              placeholder="/page or https://…"
            />
          </div>
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Heading</label>
            <input
              className={inputCls}
              value={String(props.heading ?? '')}
              onChange={(e) => set('heading', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Subheading</label>
            <input
              className={inputCls}
              value={String(props.subheading ?? '')}
              onChange={(e) => set('subheading', e.target.value)}
            />
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Button Text</label>
              <input
                className={inputCls}
                value={String(props.buttonText ?? '')}
                onChange={(e) => set('buttonText', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Button Link</label>
              <input
                className={inputCls}
                value={String(props.buttonLink ?? '')}
                onChange={(e) => set('buttonLink', e.target.value)}
              />
            </div>
          </div>
        </div>
      );

    case 'text':
    case 'rich_text':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Content (HTML)</label>
            <textarea
              rows={8}
              className="w-full border border-zinc-200 rounded-lg text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white resize-y"
              value={String(props.content ?? '')}
              onChange={(e) => set('content', e.target.value)}
            />
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="space-y-3">
          <MediaPickerModal
            open={mediaPickerOpen}
            onClose={() => setMediaPickerOpen(false)}
            onSelect={(url) => {
              set(mediaPickerTarget, url);
              setMediaPickerOpen(false);
            }}
          />
          <div>
            <label className={labelCls}>Image</label>
            {!!props.url && (
              <div className="mb-2 rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50 h-28 flex items-center justify-center">
                <img
                  src={String(props.url)}
                  alt={String(props.alt ?? '')}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
            <div className="flex gap-2">
              <input
                className={inputCls}
                value={String(props.url ?? '')}
                onChange={(e) => set('url', e.target.value)}
                placeholder="https://… or choose from library"
              />
              <button
                type="button"
                onClick={() => {
                  setMediaPickerTarget('url');
                  setMediaPickerOpen(true);
                }}
                className="shrink-0 px-3 h-8 text-xs font-medium border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-700 whitespace-nowrap"
              >
                Choose
              </button>
            </div>
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Alt text</label>
              <input
                className={inputCls}
                value={String(props.alt ?? '')}
                onChange={(e) => set('alt', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Caption</label>
              <input
                className={inputCls}
                value={String(props.caption ?? '')}
                onChange={(e) => set('caption', e.target.value)}
              />
            </div>
          </div>
        </div>
      );

    case 'banner':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Banner text</label>
            <input
              className={inputCls}
              value={String(props.text ?? '')}
              onChange={(e) => set('text', e.target.value)}
            />
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Variant</label>
              <select
                className={inputCls}
                value={String(props.variant ?? 'info')}
                onChange={(e) => set('variant', e.target.value)}
              >
                {['info', 'success', 'warning', 'error'].map((v) => (
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Position</label>
              <select
                className={inputCls}
                value={String(props.position ?? 'inline')}
                onChange={(e) => set('position', e.target.value)}
              >
                <option value="top">Top (sticky)</option>
                <option value="inline">Inline</option>
              </select>
            </div>
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Link URL</label>
              <input
                className={inputCls}
                value={String(props.link ?? '')}
                onChange={(e) => set('link', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Link label</label>
              <input
                className={inputCls}
                value={String(props.linkLabel ?? '')}
                onChange={(e) => set('linkLabel', e.target.value)}
              />
            </div>
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Background color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={String(props.backgroundColor ?? '#4F46E5')}
                  onChange={(e) => set('backgroundColor', e.target.value)}
                  className="h-8 w-10 rounded border border-zinc-200 cursor-pointer p-0.5"
                />
                <input
                  className={cn(inputCls, 'flex-1')}
                  value={String(props.backgroundColor ?? '#4F46E5')}
                  onChange={(e) => set('backgroundColor', e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Text color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={String(props.textColor ?? '#ffffff')}
                  onChange={(e) => set('textColor', e.target.value)}
                  className="h-8 w-10 rounded border border-zinc-200 cursor-pointer p-0.5"
                />
                <input
                  className={cn(inputCls, 'flex-1')}
                  value={String(props.textColor ?? '#ffffff')}
                  onChange={(e) => set('textColor', e.target.value)}
                />
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(props.dismissible)}
              onChange={(e) => set('dismissible', e.target.checked)}
              className="rounded"
            />
            <span className="text-xs text-zinc-700">Dismissible (show close button)</span>
          </label>
        </div>
      );

    case 'card_grid': {
      const cards = (props.cards as CardItem[]) ?? [];
      return (
        <div className="space-y-3">
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Columns</label>
              <select
                className={inputCls}
                value={String(props.columns ?? '3')}
                onChange={(e) => set('columns', e.target.value)}
              >
                {['2', '3', '4'].map((v) => (
                  <option key={v} value={v}>{v} columns</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Card style</label>
              <select
                className={inputCls}
                value={String(props.cardStyle ?? 'default')}
                onChange={(e) => set('cardStyle', e.target.value)}
              >
                {['default', 'elevated', 'bordered', 'flat'].map((v) => (
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          {cards.map((card, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-600">Card {i + 1}</span>
                <button
                  onClick={() =>
                    set(
                      'cards',
                      cards.filter((_, j) => j !== i),
                    )
                  }
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <input
                className={inputCls}
                placeholder="Title"
                value={card.title ?? ''}
                onChange={(e) => {
                  const next = [...cards];
                  next[i] = { ...card, title: e.target.value };
                  set('cards', next);
                }}
              />
              <input
                className={inputCls}
                placeholder="Description"
                value={card.description ?? ''}
                onChange={(e) => {
                  const next = [...cards];
                  next[i] = { ...card, description: e.target.value };
                  set('cards', next);
                }}
              />
              <input
                className={inputCls}
                placeholder="Image URL (https://…)"
                value={card.imageUrl ?? ''}
                onChange={(e) => {
                  const next = [...cards];
                  next[i] = { ...card, imageUrl: e.target.value };
                  set('cards', next);
                }}
              />
              <div className={rowCls}>
                <input
                  className={inputCls}
                  placeholder="Link URL"
                  value={card.link ?? ''}
                  onChange={(e) => {
                    const next = [...cards];
                    next[i] = { ...card, link: e.target.value };
                    set('cards', next);
                  }}
                />
                <input
                  className={inputCls}
                  placeholder="Link label"
                  value={(card as CardItem & { linkLabel?: string }).linkLabel ?? ''}
                  onChange={(e) => {
                    const next = [...cards] as Array<CardItem & { linkLabel?: string }>;
                    next[i] = { ...card, linkLabel: e.target.value };
                    set('cards', next);
                  }}
                />
              </div>
            </div>
          ))}
          <button
            onClick={() =>
              set('cards', [
                ...cards,
                { title: 'New Card', description: '', imageUrl: '', link: '', linkLabel: '' },
              ])
            }
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800"
          >
            <Plus size={13} /> Add card
          </button>
        </div>
      );
    }

    case 'spacer':
      return (
        <div>
          <label className={labelCls}>Height</label>
          <select
            className={inputCls}
            value={String(props.height ?? 'md')}
            onChange={(e) => set('height', e.target.value)}
          >
            {['xs', 'sm', 'md', 'lg', 'xl'].map((h) => (
              <option key={h} value={h}>
                {h.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      );

    case 'html':
      return (
        <div>
          <label className={labelCls}>HTML code</label>
          <textarea
            rows={8}
            className="w-full border border-zinc-200 rounded-lg text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white resize-y"
            value={String(props.code ?? '')}
            onChange={(e) => set('code', e.target.value)}
          />
        </div>
      );

    case 'loyalty_points':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Widget title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(props.showTier)}
              onChange={(e) => set('showTier', e.target.checked)}
              className="rounded"
            />
            <span className="text-xs text-zinc-700">Show tier badge</span>
          </label>
        </div>
      );

    case 'loyalty_rewards':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Widget title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Max items</label>
            <input
              type="number"
              min={1}
              max={20}
              className={inputCls}
              value={Number(props.maxItems ?? 6)}
              onChange={(e) => set('maxItems', parseInt(e.target.value, 10))}
            />
          </div>
        </div>
      );

    case 'loyalty_tier':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Widget title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(props.showProgress)}
              onChange={(e) => set('showProgress', e.target.checked)}
              className="rounded"
            />
            <span className="text-xs text-zinc-700">Show progress bar</span>
          </label>
        </div>
      );

    case 'feature_grid': {
      type FeatureItem = { icon: string; title: string; description: string };
      const features = (props.features as FeatureItem[]) ?? [];
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Section title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Subtitle</label>
            <input
              className={inputCls}
              value={String(props.subtitle ?? '')}
              onChange={(e) => set('subtitle', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Columns</label>
            <select
              className={inputCls}
              value={String(props.columns ?? '3')}
              onChange={(e) => set('columns', e.target.value)}
            >
              {['2', '3', '4'].map((v) => (
                <option key={v} value={v}>{v} columns</option>
              ))}
            </select>
          </div>
          {features.map((feat, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-600">Feature {i + 1}</span>
                <button
                  onClick={() => set('features', features.filter((_, j) => j !== i))}
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <input
                className={inputCls}
                placeholder="Icon name (Lucide, e.g. Zap)"
                value={feat.icon ?? ''}
                onChange={(e) => {
                  const next = [...features];
                  next[i] = { ...feat, icon: e.target.value };
                  set('features', next);
                }}
              />
              <input
                className={inputCls}
                placeholder="Title"
                value={feat.title ?? ''}
                onChange={(e) => {
                  const next = [...features];
                  next[i] = { ...feat, title: e.target.value };
                  set('features', next);
                }}
              />
              <input
                className={inputCls}
                placeholder="Description"
                value={feat.description ?? ''}
                onChange={(e) => {
                  const next = [...features];
                  next[i] = { ...feat, description: e.target.value };
                  set('features', next);
                }}
              />
            </div>
          ))}
          <button
            onClick={() =>
              set('features', [...features, { icon: '', title: 'New Feature', description: '' }])
            }
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800"
          >
            <Plus size={13} /> Add feature
          </button>
        </div>
      );
    }

    case 'testimonial': {
      type TestimonialItem = { quote: string; author: string; authorTitle: string; avatarUrl: string; rating: number };
      const testimonials = (props.testimonials as TestimonialItem[]) ?? [];
      return (
        <div className="space-y-3">
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Layout</label>
              <select
                className={inputCls}
                value={String(props.layout ?? 'single')}
                onChange={(e) => set('layout', e.target.value)}
              >
                <option value="single">Single</option>
                <option value="carousel">Carousel</option>
                <option value="grid">Grid</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Section title</label>
              <input
                className={inputCls}
                value={String(props.title ?? '')}
                onChange={(e) => set('title', e.target.value)}
              />
            </div>
          </div>
          {testimonials.map((t, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-600">Testimonial {i + 1}</span>
                <button
                  onClick={() => set('testimonials', testimonials.filter((_, j) => j !== i))}
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <textarea
                rows={3}
                className="w-full border border-zinc-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white resize-y"
                placeholder="Quote…"
                value={t.quote ?? ''}
                onChange={(e) => {
                  const next = [...testimonials];
                  next[i] = { ...t, quote: e.target.value };
                  set('testimonials', next);
                }}
              />
              <div className={rowCls}>
                <input
                  className={inputCls}
                  placeholder="Author name"
                  value={t.author ?? ''}
                  onChange={(e) => {
                    const next = [...testimonials];
                    next[i] = { ...t, author: e.target.value };
                    set('testimonials', next);
                  }}
                />
                <input
                  className={inputCls}
                  placeholder="Author title / company"
                  value={t.authorTitle ?? ''}
                  onChange={(e) => {
                    const next = [...testimonials];
                    next[i] = { ...t, authorTitle: e.target.value };
                    set('testimonials', next);
                  }}
                />
              </div>
              <div className={rowCls}>
                <input
                  className={inputCls}
                  placeholder="Avatar URL (https://…)"
                  value={t.avatarUrl ?? ''}
                  onChange={(e) => {
                    const next = [...testimonials];
                    next[i] = { ...t, avatarUrl: e.target.value };
                    set('testimonials', next);
                  }}
                />
                <div>
                  <label className={labelCls}>Rating (1–5)</label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    className={inputCls}
                    value={t.rating ?? 5}
                    onChange={(e) => {
                      const next = [...testimonials];
                      next[i] = { ...t, rating: parseInt(e.target.value, 10) };
                      set('testimonials', next);
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={() =>
              set('testimonials', [
                ...testimonials,
                { quote: '', author: '', authorTitle: '', avatarUrl: '', rating: 5 },
              ])
            }
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800"
          >
            <Plus size={13} /> Add testimonial
          </button>
        </div>
      );
    }

    case 'pricing_table': {
      type PricingPlan = { name: string; price: string; period: string; features: string[]; highlighted: boolean; ctaText: string; ctaLink: string };
      const plans = (props.plans as PricingPlan[]) ?? [];
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Section title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          {plans.map((plan, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-600">Plan {i + 1}</span>
                <button
                  onClick={() => set('plans', plans.filter((_, j) => j !== i))}
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <div className={rowCls}>
                <input
                  className={inputCls}
                  placeholder="Plan name"
                  value={plan.name ?? ''}
                  onChange={(e) => {
                    const next = [...plans];
                    next[i] = { ...plan, name: e.target.value };
                    set('plans', next);
                  }}
                />
                <div className="flex gap-1.5">
                  <input
                    className={cn(inputCls, 'w-20')}
                    placeholder="Price"
                    value={plan.price ?? ''}
                    onChange={(e) => {
                      const next = [...plans];
                      next[i] = { ...plan, price: e.target.value };
                      set('plans', next);
                    }}
                  />
                  <select
                    className={cn(inputCls, 'flex-1')}
                    value={plan.period ?? 'monthly'}
                    onChange={(e) => {
                      const next = [...plans];
                      next[i] = { ...plan, period: e.target.value };
                      set('plans', next);
                    }}
                  >
                    <option value="monthly">/mo</option>
                    <option value="yearly">/yr</option>
                    <option value="one-time">once</option>
                  </select>
                </div>
              </div>
              <div className={rowCls}>
                <input
                  className={inputCls}
                  placeholder="CTA text"
                  value={plan.ctaText ?? ''}
                  onChange={(e) => {
                    const next = [...plans];
                    next[i] = { ...plan, ctaText: e.target.value };
                    set('plans', next);
                  }}
                />
                <input
                  className={inputCls}
                  placeholder="CTA link"
                  value={plan.ctaLink ?? ''}
                  onChange={(e) => {
                    const next = [...plans];
                    next[i] = { ...plan, ctaLink: e.target.value };
                    set('plans', next);
                  }}
                />
              </div>
              <div>
                <label className={labelCls}>Features (one per line)</label>
                <textarea
                  rows={3}
                  className="w-full border border-zinc-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white resize-y"
                  value={(plan.features ?? []).join('\n')}
                  onChange={(e) => {
                    const next = [...plans];
                    next[i] = { ...plan, features: e.target.value.split('\n') };
                    set('plans', next);
                  }}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(plan.highlighted)}
                  onChange={(e) => {
                    const next = [...plans];
                    next[i] = { ...plan, highlighted: e.target.checked };
                    set('plans', next);
                  }}
                  className="rounded"
                />
                <span className="text-xs text-zinc-700">Highlight this plan</span>
              </label>
            </div>
          ))}
          <button
            onClick={() =>
              set('plans', [
                ...plans,
                { name: 'New Plan', price: '0', period: 'monthly', features: [], highlighted: false, ctaText: 'Get started', ctaLink: '' },
              ])
            }
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800"
          >
            <Plus size={13} /> Add plan
          </button>
        </div>
      );
    }

    case 'form': {
      type FormField = { label: string; fieldType: string; required: boolean; placeholder: string; options: string };
      const fields = (props.fields as FormField[]) ?? [];
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Form title</label>
            <input
              className={inputCls}
              value={String(props.formTitle ?? '')}
              onChange={(e) => set('formTitle', e.target.value)}
            />
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Submit button text</label>
              <input
                className={inputCls}
                value={String(props.submitButtonText ?? 'Submit')}
                onChange={(e) => set('submitButtonText', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Success message</label>
              <input
                className={inputCls}
                value={String(props.successMessage ?? 'Thank you!')}
                onChange={(e) => set('successMessage', e.target.value)}
              />
            </div>
          </div>
          {fields.map((field, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-600">Field {i + 1}</span>
                <button
                  onClick={() => set('fields', fields.filter((_, j) => j !== i))}
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <div className={rowCls}>
                <input
                  className={inputCls}
                  placeholder="Label"
                  value={field.label ?? ''}
                  onChange={(e) => {
                    const next = [...fields];
                    next[i] = { ...field, label: e.target.value };
                    set('fields', next);
                  }}
                />
                <select
                  className={inputCls}
                  value={field.fieldType ?? 'text'}
                  onChange={(e) => {
                    const next = [...fields];
                    next[i] = { ...field, fieldType: e.target.value };
                    set('fields', next);
                  }}
                >
                  {['text', 'email', 'phone', 'textarea', 'select', 'checkbox'].map((t) => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className={rowCls}>
                <input
                  className={inputCls}
                  placeholder="Placeholder text"
                  value={field.placeholder ?? ''}
                  onChange={(e) => {
                    const next = [...fields];
                    next[i] = { ...field, placeholder: e.target.value };
                    set('fields', next);
                  }}
                />
                {field.fieldType === 'select' && (
                  <input
                    className={inputCls}
                    placeholder="Options (comma-separated)"
                    value={field.options ?? ''}
                    onChange={(e) => {
                      const next = [...fields];
                      next[i] = { ...field, options: e.target.value };
                      set('fields', next);
                    }}
                  />
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(field.required)}
                  onChange={(e) => {
                    const next = [...fields];
                    next[i] = { ...field, required: e.target.checked };
                    set('fields', next);
                  }}
                  className="rounded"
                />
                <span className="text-xs text-zinc-700">Required</span>
              </label>
            </div>
          ))}
          <button
            onClick={() =>
              set('fields', [
                ...fields,
                { label: 'New field', fieldType: 'text', required: false, placeholder: '', options: '' },
              ])
            }
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800"
          >
            <Plus size={13} /> Add field
          </button>
        </div>
      );
    }

    case 'video':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Video URL (YouTube or Vimeo)</label>
            <input
              className={inputCls}
              value={String(props.videoUrl ?? '')}
              onChange={(e) => set('videoUrl', e.target.value)}
              placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
            />
          </div>
          <div>
            <label className={labelCls}>Title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Caption</label>
            <input
              className={inputCls}
              value={String(props.caption ?? '')}
              onChange={(e) => set('caption', e.target.value)}
            />
          </div>
          <div className={rowCls}>
            <div>
              <label className={labelCls}>Aspect ratio</label>
              <select
                className={inputCls}
                value={String(props.aspectRatio ?? '16:9')}
                onChange={(e) => set('aspectRatio', e.target.value)}
              >
                {['16:9', '4:3', '1:1'].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(props.autoplay)}
                  onChange={(e) => set('autoplay', e.target.checked)}
                  className="rounded"
                />
                <span className="text-xs text-zinc-700">Autoplay</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={props.controls !== false}
                  onChange={(e) => set('controls', e.target.checked)}
                  className="rounded"
                />
                <span className="text-xs text-zinc-700">Show controls</span>
              </label>
            </div>
          </div>
        </div>
      );

    case 'accordion': {
      type AccordionItem = { question: string; answer: string };
      const items = (props.items as AccordionItem[]) ?? [];
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Section title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          {items.map((item, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-600">Item {i + 1}</span>
                <button
                  onClick={() => set('items', items.filter((_, j) => j !== i))}
                  className="text-zinc-400 hover:text-red-500"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <input
                className={inputCls}
                placeholder="Question"
                value={item.question ?? ''}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...item, question: e.target.value };
                  set('items', next);
                }}
              />
              <textarea
                rows={3}
                className="w-full border border-zinc-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white resize-y"
                placeholder="Answer…"
                value={item.answer ?? ''}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...item, answer: e.target.value };
                  set('items', next);
                }}
              />
            </div>
          ))}
          <button
            onClick={() =>
              set('items', [...items, { question: 'New question', answer: '' }])
            }
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800"
          >
            <Plus size={13} /> Add item
          </button>
        </div>
      );
    }

    case 'countdown':
      return (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Title</label>
            <input
              className={inputCls}
              value={String(props.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Target date & time</label>
            <input
              type="datetime-local"
              className={inputCls}
              value={props.targetDate ? String(props.targetDate).slice(0, 16) : ''}
              onChange={(e) => set('targetDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
            />
          </div>
          <div>
            <label className={labelCls}>Expired message</label>
            <input
              className={inputCls}
              value={String(props.expiredMessage ?? 'The event has ended.')}
              onChange={(e) => set('expiredMessage', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {(['showDays', 'showHours', 'showMinutes', 'showSeconds'] as const).map((key) => (
              <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={props[key] !== false}
                  onChange={(e) => set(key, e.target.checked)}
                  className="rounded"
                />
                <span className="text-xs text-zinc-700">{key.replace('show', '')}</span>
              </label>
            ))}
          </div>
        </div>
      );

    case 'row': {
      const colCount = (props.columnCount as number) ?? 2;
      const presets = COLUMN_PRESETS[colCount] ?? COLUMN_PRESETS[2];
      const currentWidths = (props.columnWidths as string[]) ?? presets[0].widths;
      return (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Columns</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    const defaultWidths = COLUMN_PRESETS[n]![0].widths;
                    set('columnCount', n);
                    set('columnWidths', defaultWidths);
                  }}
                  className={cn(
                    'w-9 h-9 rounded-lg border text-sm font-medium transition-colors',
                    colCount === n
                      ? 'border-zinc-800 bg-zinc-800 text-white'
                      : 'border-zinc-200 text-zinc-600 hover:border-zinc-400',
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          {colCount > 1 && (
            <div>
              <label className={labelCls}>Column widths</label>
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => {
                  const isActive = JSON.stringify(currentWidths) === JSON.stringify(preset.widths);
                  return (
                    <button
                      key={preset.label}
                      onClick={() => set('columnWidths', preset.widths)}
                      className={cn(
                        'px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors',
                        isActive
                          ? 'border-zinc-800 bg-zinc-800 text-white'
                          : 'border-zinc-200 text-zinc-600 hover:border-zinc-400',
                      )}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Gap</label>
              <select
                className={inputCls}
                value={String(props.gap ?? 'md')}
                onChange={(e) => set('gap', e.target.value)}
              >
                <option value="none">None</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Vertical align</label>
              <select
                className={inputCls}
                value={String(props.verticalAlign ?? 'top')}
                onChange={(e) => set('verticalAlign', e.target.value)}
              >
                <option value="top">Top</option>
                <option value="center">Center</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
          </div>
        </div>
      );
    }

    default:
      return <p className="text-xs text-zinc-400">Use custom block definition fields below.</p>;
  }
}

// ─── Dynamic block definition editor ─────────────────────────────────────────

type CollectionPickerItem = {
  id: string;
  slug: string;
  name: string;
  fieldsJson: { key: string; label: string; type: string }[];
};

function CollectionSelectField({
  value,
  onChange,
  spaceId,
}: {
  value: string;
  onChange: (v: string) => void;
  spaceId?: string;
}) {
  const { data: collections = [], isLoading } = useQuery<CollectionPickerItem[]>({
    queryKey: ['cms-collections-picker', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/collections`).then((r) => r.data),
    enabled: !!spaceId,
    staleTime: 60_000,
  });
  return (
    <select
      className="w-full border border-zinc-200 rounded-lg text-sm px-3 h-8 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={isLoading}
    >
      <option value="">{isLoading ? 'Loading…' : '— select collection —'}</option>
      {collections.map((c) => (
        <option key={c.id} value={c.slug}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

function FieldSelectField({
  value,
  onChange,
  spaceId,
  collectionSlug,
}: {
  value: string;
  onChange: (v: string) => void;
  spaceId?: string;
  collectionSlug: string;
}) {
  const { data: collections = [] } = useQuery<CollectionPickerItem[]>({
    queryKey: ['cms-collections-picker', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/collections`).then((r) => r.data),
    enabled: !!spaceId,
    staleTime: 60_000,
  });
  const collection = collections.find((c) => c.slug === collectionSlug);
  const fields = collection?.fieldsJson ?? [];
  return (
    <select
      className="w-full border border-zinc-200 rounded-lg text-sm px-3 h-8 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={!collectionSlug}
    >
      <option value="">
        {collectionSlug ? '— select field —' : '— select a collection first —'}
      </option>
      {fields.map((f) => (
        <option key={f.key} value={f.key}>
          {f.label} ({f.key})
        </option>
      ))}
    </select>
  );
}

function DynamicBlockEditor({
  def,
  props,
  spaceId,
  onChange,
}: {
  def: BlockDef;
  props: Record<string, unknown>;
  spaceId?: string;
  onChange: (props: Record<string, unknown>) => void;
}) {
  function set(key: string, value: unknown) {
    onChange({ ...props, [key]: value });
  }
  const labelCls = 'block text-xs font-medium text-zinc-600 mb-1';
  const inputCls =
    'w-full border border-zinc-200 rounded-lg text-sm px-3 h-8 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white';

  if (def.fieldsJson.length === 0) {
    return <p className="text-xs text-zinc-400">No configurable fields for this block.</p>;
  }

  return (
    <div className="space-y-3">
      {def.fieldsJson.map((field) => {
        const val = props[field.key] ?? '';
        switch (field.type) {
          case 'boolean':
            return (
              <label key={field.key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(val)}
                  onChange={(e) => set(field.key, e.target.checked)}
                  className="rounded border-zinc-300"
                />
                <span className="text-xs text-zinc-700">{field.label}</span>
              </label>
            );
          case 'number':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <input
                  type="number"
                  className={inputCls}
                  value={String(val ?? '')}
                  onChange={(e) => set(field.key, parseFloat(e.target.value))}
                />
              </div>
            );
          case 'select':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <select
                  className={inputCls}
                  value={String(val ?? '')}
                  onChange={(e) => set(field.key, e.target.value)}
                >
                  <option value="">— select —</option>
                  {(field.options ?? []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            );
          case 'collection_select':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <CollectionSelectField
                  value={String(val ?? '')}
                  onChange={(v) => set(field.key, v)}
                  spaceId={spaceId}
                />
              </div>
            );
          case 'field_select':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <FieldSelectField
                  value={String(val ?? '')}
                  onChange={(v) => set(field.key, v)}
                  spaceId={spaceId}
                  collectionSlug={String(props[field.collectionKey ?? ''] ?? '')}
                />
              </div>
            );
          case 'rich_text':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <textarea
                  rows={4}
                  className="w-full border border-zinc-200 rounded-lg text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white resize-y text-xs"
                  value={String(val ?? '')}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              </div>
            );
          default:
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <input
                  className={inputCls}
                  value={String(val ?? '')}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              </div>
            );
        }
      })}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsPageEditorPage() {
  const { spaceId, pageId } = useParams<{ spaceId: string; pageId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [blocks, setBlocks] = useState<CmsBlock[]>([]);
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  // null = add flat block; set = add into row column
  const [addBlockTarget, setAddBlockTarget] = useState<{ rowId: string; colIndex: number } | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);
  const [metaForm, setMetaForm] = useState({
    title: '',
    metaTitle: '',
    metaDescription: '',
    isPublic: false,
    isTemplate: false,
    loyaltyMembersOnly: false,
    layoutId: '' as string,
  });

  const BASE = `/api/v1/cms/spaces/${spaceId}/pages/${pageId}`;

  const { data: page, isLoading } = useQuery<CmsPage>({
    queryKey: ['cms-page', spaceId, pageId],
    queryFn: () => api.get(BASE).then((r) => r.data),
    enabled: !!spaceId && !!pageId,
  });

  useEffect(() => {
    if (page && !dirty) {
      setBlocks(page.blocks ?? []);
      setMetaForm({
        title: page.title,
        metaTitle: page.metaTitle ?? '',
        metaDescription: page.metaDescription ?? '',
        isPublic: page.isPublic,
        isTemplate: page.isTemplate ?? false,
        loyaltyMembersOnly: page.loyaltyMembersOnly,
        layoutId: page.layoutId ?? '',
      });
    }
  }, [page, dirty]);

  const { data: versions = [] } = useQuery<Version[]>({
    queryKey: ['cms-page-versions', spaceId, pageId],
    queryFn: () => api.get(`${BASE}/versions`).then((r) => r.data),
    enabled: !!spaceId && !!pageId && showVersions,
  });

  const { data: blockDefs = [] } = useQuery<BlockDef[]>({
    queryKey: ['cms-block-definitions'],
    queryFn: () => api.get('/api/v1/cms/block-definitions').then((r) => r.data),
  });

  const { data: layouts = [] } = useQuery<CmsLayout[]>({
    queryKey: ['cms-layouts', spaceId],
    queryFn: () =>
      api.get(`/api/v1/cms/spaces/${spaceId}/layouts`).then((r) => r.data),
    enabled: !!spaceId,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api.put(`${BASE}/blocks`, {
        blocks: blocks.map((b, idx) => ({
          blockDefinitionId: b.blockDefinitionId ?? null,
          parentBlockId: b.parentBlockId ?? null,
          sortOrder: idx,
          props: b.props,
          loyaltyMembersOnly: b.loyaltyMembersOnly ?? false,
        })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-page', spaceId, pageId] });
      setDirty(false);
      toast.success('Blocks saved');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to save blocks')),
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (dirty) {
        await api.put(`${BASE}/blocks`, {
          blocks: blocks.map((b, idx) => ({
            blockDefinitionId: b.blockDefinitionId ?? null,
            parentBlockId: b.parentBlockId ?? null,
            sortOrder: idx,
            props: b.props,
            loyaltyMembersOnly: b.loyaltyMembersOnly ?? false,
          })),
        });
      }
      return api.post(`${BASE}/publish`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-page', spaceId, pageId] });
      qc.invalidateQueries({ queryKey: ['cms-page-versions', spaceId, pageId] });
      setDirty(false);
      toast.success('Page published');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to publish')),
  });

  const metaMutation = useMutation({
    mutationFn: (body: object) => api.patch(BASE, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-page', spaceId, pageId] });
      qc.invalidateQueries({ queryKey: ['cms-pages', spaceId] });
      setEditingMeta(false);
      toast.success('Page updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const previewMutation = useMutation({
    mutationFn: () => api.post(`${BASE}/preview-token`).then((r) => r.data as { token: string }),
    onSuccess: ({ token }) => {
      const portalBase = window.location.origin.replace(/admin\./, '');
      window.open(`${portalBase}/preview/${token}`, '_blank', 'noopener,noreferrer');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to generate preview link')),
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) => api.post(`${BASE}/versions/${versionId}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-page', spaceId, pageId] });
      qc.invalidateQueries({ queryKey: ['cms-page-versions', spaceId, pageId] });
      setDirty(false);
      setRestoringVersionId(null);
      setShowVersions(false);
      toast.success('Version restored');
    },
    onError: (err) => {
      setRestoringVersionId(null);
      toast.error(extractError(err, 'Failed to restore version'));
    },
  });

  function addBlock(def: BlockDef) {
    if (addBlockTarget) {
      // Add block inside a row column
      const { rowId, colIndex } = addBlockTarget;
      const siblings = blocks.filter(
        (b) => b.parentBlockId === rowId && ((b.props._col as number) ?? 0) === colIndex,
      );
      const newBlock: CmsBlock = {
        id: `new-${Date.now()}-${Math.random()}`,
        blockDefinitionId: def.id,
        sortOrder: siblings.length,
        props: { _col: colIndex },
        parentBlockId: rowId,
      };
      setBlocks((prev) => [...prev, newBlock]);
      setExpandedBlock(newBlock.id);
      setAddBlockTarget(null);
    } else {
      // Add flat root block
      const rootCount = blocks.filter((b) => !b.parentBlockId).length;
      const newBlock: CmsBlock = {
        id: `new-${Date.now()}-${Math.random()}`,
        blockDefinitionId: def.id,
        sortOrder: rootCount,
        props: {},
      };
      setBlocks((prev) => [...prev, newBlock]);
      setExpandedBlock(newBlock.id);
    }
    setDirty(true);
    setShowAddBlock(false);
  }

  function addRow(columnCount: number, columnWidths: string[]) {
    const rowDef = blockDefs.find((d) => d.slug === 'row');
    if (!rowDef) return;
    const rootCount = blocks.filter((b) => !b.parentBlockId).length;
    const newRow: CmsBlock = {
      id: `new-row-${Date.now()}-${Math.random()}`,
      blockDefinitionId: rowDef.id,
      sortOrder: rootCount,
      props: { columnCount, columnWidths, gap: 'md', verticalAlign: 'top' },
      parentBlockId: null,
    };
    setBlocks((prev) => [...prev, newRow]);
    setExpandedBlock(newRow.id);
    setDirty(true);
  }

  function removeBlock(blockId: string) {
    // Also remove child blocks if this is a row
    setBlocks((prev) => prev.filter((b) => b.id !== blockId && b.parentBlockId !== blockId));
    setDirty(true);
  }

  function moveRootBlock(blockId: string, dir: 'up' | 'down') {
    setBlocks((prev) => {
      const roots = prev.filter((b) => !b.parentBlockId).sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = roots.findIndex((b) => b.id === blockId);
      if (idx < 0) return prev;
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= roots.length) return prev;
      const newRoots = [...roots];
      [newRoots[idx], newRoots[swap]] = [newRoots[swap], newRoots[idx]];
      // Reconstruct flat array: roots in new order, each followed by their children
      const result: CmsBlock[] = [];
      for (const root of newRoots) {
        result.push(root);
        const children = prev
          .filter((b) => b.parentBlockId === root.id)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        result.push(...children);
      }
      return result;
    });
    setDirty(true);
  }

  function moveBlockInColumn(blockId: string, dir: 'up' | 'down') {
    setBlocks((prev) => {
      const block = prev.find((b) => b.id === blockId);
      if (!block?.parentBlockId) return prev;
      const colIndex = (block.props._col as number) ?? 0;
      const siblings = prev
        .filter(
          (b) =>
            b.parentBlockId === block.parentBlockId && ((b.props._col as number) ?? 0) === colIndex,
        )
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = siblings.findIndex((b) => b.id === blockId);
      if (idx < 0) return prev;
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= siblings.length) return prev;
      const swapBlock = siblings[swap];
      return prev.map((b) => {
        if (b.id === blockId) return { ...b, sortOrder: swapBlock.sortOrder };
        if (b.id === swapBlock.id) return { ...b, sortOrder: block.sortOrder };
        return b;
      });
    });
    setDirty(true);
  }

  // Legacy alias — used for flat root blocks (keeps existing call sites working)
  function moveBlock(blockId: string, dir: 'up' | 'down') {
    const block = blocks.find((b) => b.id === blockId);
    if (block?.parentBlockId) {
      moveBlockInColumn(blockId, dir);
    } else {
      moveRootBlock(blockId, dir);
    }
  }

  function updateBlockProps(blockId: string, props: Record<string, unknown>) {
    setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, props } : b)));
    setDirty(true);
  }

  function updateBlockLoyalty(blockId: string, loyaltyMembersOnly: boolean) {
    setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, loyaltyMembersOnly } : b)));
    setDirty(true);
  }

  // Group block defs by category
  const defsByCategory = blockDefs
    .filter((d) => d.isActive)
    .reduce<Record<string, BlockDef[]>>((acc, d) => {
      const cat = d.category || 'Other';
      (acc[cat] ??= []).push(d);
      return acc;
    }, {});

  function getBlockLabel(block: CmsBlock): string {
    const def = blockDefs.find((d) => d.id === block.blockDefinitionId);
    return def?.name ?? 'Block';
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
      </div>
    );
  }

  if (!page) {
    return <p className="text-zinc-500">Page not found.</p>;
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <Link
          to={`/spaces/${spaceId}/pages`}
          className="hover:text-zinc-600 flex items-center gap-1"
        >
          <ChevronLeft size={13} /> Pages
        </Link>
        <span>/</span>
        <span className="text-zinc-700 font-medium truncate max-w-[200px]">{page.title}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-zinc-900">{page.title}</h1>
            <Badge variant={page.status === 'published' ? 'success' : 'default'}>
              {page.status}
            </Badge>
            {page.isPublic && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <Globe size={11} /> Public
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">
            /p/{page.slug}
            {page.publishedAt && <> · Published {fmtDateTime(page.publishedAt)}</>}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowVersions(true)}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1.5 rounded-md hover:bg-zinc-100 transition-colors"
          >
            <History size={13} /> Versions
          </button>
          <button
            onClick={() => previewMutation.mutate()}
            disabled={previewMutation.isPending}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1.5 rounded-md hover:bg-zinc-100 transition-colors disabled:opacity-50"
          >
            <ExternalLink size={13} /> Preview
          </button>
          <button
            onClick={() => setEditingMeta(true)}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1.5 rounded-md hover:bg-zinc-100 transition-colors"
          >
            <Eye size={13} /> Settings
          </button>
          {dirty && (
            <Button
              variant="ghost"
              size="sm"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              <Save size={13} className="mr-1.5" /> Save draft
            </Button>
          )}
          <Button
            size="sm"
            loading={publishMutation.isPending}
            onClick={() => publishMutation.mutate()}
          >
            <Send size={13} className="mr-1.5" />
            {page.status === 'published' ? 'Re-publish' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Block editor */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <div className="space-y-2">
          {blocks.length === 0 && (
            <div className="border-2 border-dashed border-zinc-200 rounded-xl p-10 text-center">
              <p className="text-sm text-zinc-500 mb-3">
                No blocks yet. Add your first block to start building.
              </p>
              <div className="flex gap-2 justify-center">
                <Button size="sm" onClick={() => { setAddBlockTarget(null); setShowAddBlock(true); }}>
                  <Plus size={13} className="mr-1.5" /> Add block
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowAddRow(true)}>
                  <Columns2 size={13} className="mr-1.5" /> Add row
                </Button>
              </div>
            </div>
          )}

          {(() => {
            const rootBlocks = blocks
              .filter((b) => !b.parentBlockId)
              .sort((a, b) => a.sortOrder - b.sortOrder);
            return rootBlocks.map((block, rootIdx) => {
              const isExpanded = expandedBlock === block.id;
              const def = blockDefs.find((d) => d.id === block.blockDefinitionId);
              const isRow = def?.slug === 'row';

              if (isRow) {
                // ─── Row block ───────────────────────────────────────────────
                const colCount = (block.props.columnCount as number) ?? 2;
                const columnWidths = (block.props.columnWidths as string[]) ?? ['1/2', '1/2'];
                const colChildren = (colIdx: number) =>
                  blocks
                    .filter(
                      (b) =>
                        b.parentBlockId === block.id &&
                        ((b.props._col as number) ?? 0) === colIdx,
                    )
                    .sort((a, b) => a.sortOrder - b.sortOrder);

                return (
                  <div
                    key={block.id}
                    className="bg-white border-2 border-dashed border-zinc-300 rounded-xl overflow-hidden"
                  >
                    {/* Row header */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                      <Columns2 size={13} className="text-zinc-400 flex-shrink-0" />
                      <span className="text-xs font-medium text-zinc-500 flex-1">
                        Row — {colCount} column{colCount > 1 ? 's' : ''}
                        {colCount > 1 && (
                          <span className="ml-1 text-zinc-400">
                            ({columnWidths.join(' + ')})
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <button
                          title="Row settings"
                          onClick={() => setExpandedBlock(isExpanded ? null : block.id)}
                          className={cn(
                            'p-1 rounded transition-colors',
                            isExpanded
                              ? 'text-zinc-700 bg-zinc-200'
                              : 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100',
                          )}
                        >
                          <Settings2 size={12} />
                        </button>
                        <button
                          onClick={() => moveRootBlock(block.id, 'up')}
                          disabled={rootIdx === 0}
                          className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 rounded"
                        >
                          <ChevronUp size={13} />
                        </button>
                        <button
                          onClick={() => moveRootBlock(block.id, 'down')}
                          disabled={rootIdx === rootBlocks.length - 1}
                          className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 rounded"
                        >
                          <ChevronDown size={13} />
                        </button>
                        <button
                          onClick={() => removeBlock(block.id)}
                          className="p-1 text-zinc-400 hover:text-red-500 rounded"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    {/* Row config (collapsible) */}
                    {isExpanded && def && (
                      <div className="px-4 pb-4 pt-2 border-b border-zinc-100 bg-zinc-50/50">
                        <BuiltinBlockEditor
                          slug="row"
                          props={block.props}
                          onChange={(props) => updateBlockProps(block.id, props)}
                        />
                      </div>
                    )}

                    {/* Columns */}
                    <div
                      className={cn(
                        'grid gap-px bg-zinc-200',
                        colCount === 1 && 'grid-cols-1',
                        colCount === 2 && 'grid-cols-2',
                        colCount === 3 && 'grid-cols-3',
                        colCount === 4 && 'grid-cols-4',
                      )}
                    >
                      {Array.from({ length: colCount }, (_, colIdx) => {
                        const colItems = colChildren(colIdx);
                        return (
                          <div key={colIdx} className="bg-white p-2 space-y-1.5 min-h-[80px]">
                            <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider px-1">
                              Col {colIdx + 1}
                              {columnWidths[colIdx] && (
                                <span className="ml-1 text-zinc-300">({columnWidths[colIdx]})</span>
                              )}
                            </p>
                            {colItems.map((child, childIdx) => {
                              const childIsExpanded = expandedBlock === child.id;
                              const childDef = blockDefs.find(
                                (d) => d.id === child.blockDefinitionId,
                              );
                              return (
                                <div
                                  key={child.id}
                                  className="bg-zinc-50 border border-zinc-200 rounded-lg overflow-hidden"
                                >
                                  <div
                                    className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-zinc-100 transition-colors"
                                    onClick={() =>
                                      setExpandedBlock(childIsExpanded ? null : child.id)
                                    }
                                  >
                                    <GripVertical size={12} className="text-zinc-300 flex-shrink-0" />
                                    <span className="text-xs font-medium text-zinc-700 flex-1 truncate">
                                      {getBlockLabel(child)}
                                    </span>
                                    <div className="flex items-center gap-0.5">
                                      <button
                                        title={
                                          child.loyaltyMembersOnly
                                            ? 'Members only'
                                            : 'Public'
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateBlockLoyalty(child.id, !child.loyaltyMembersOnly);
                                        }}
                                        className={cn(
                                          'p-0.5 rounded transition-colors',
                                          child.loyaltyMembersOnly
                                            ? 'text-amber-500'
                                            : 'text-zinc-300 hover:text-zinc-500',
                                        )}
                                      >
                                        <Lock size={11} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          moveBlockInColumn(child.id, 'up');
                                        }}
                                        disabled={childIdx === 0}
                                        className="p-0.5 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 rounded"
                                      >
                                        <ChevronUp size={12} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          moveBlockInColumn(child.id, 'down');
                                        }}
                                        disabled={childIdx === colItems.length - 1}
                                        className="p-0.5 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 rounded"
                                      >
                                        <ChevronDown size={12} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeBlock(child.id);
                                        }}
                                        className="p-0.5 text-zinc-400 hover:text-red-500 rounded"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  </div>
                                  {childIsExpanded && childDef && (
                                    <div className="px-3 pb-3 pt-1.5 border-t border-zinc-100">
                                      <BuiltinBlockEditor
                                        slug={childDef.slug}
                                        props={child.props}
                                        onChange={(props) => updateBlockProps(child.id, props)}
                                      />
                                      <DynamicBlockEditor
                                        def={childDef}
                                        props={child.props}
                                        spaceId={spaceId}
                                        onChange={(props) => updateBlockProps(child.id, props)}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <button
                              onClick={() => {
                                setAddBlockTarget({ rowId: block.id, colIndex: colIdx });
                                setShowAddBlock(true);
                              }}
                              className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-600 w-full px-1.5 py-1 border border-dashed border-zinc-200 rounded-lg justify-center hover:border-zinc-300 transition-colors"
                            >
                              <Plus size={11} /> Add block
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // ─── Flat block ──────────────────────────────────────────────
              return (
                <div
                  key={block.id}
                  className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
                >
                  <div
                    className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-zinc-50 transition-colors"
                    onClick={() => setExpandedBlock(isExpanded ? null : block.id)}
                  >
                    <GripVertical size={14} className="text-zinc-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-zinc-800">
                        {getBlockLabel(block)}
                      </span>
                      <span className="ml-2 text-xs text-zinc-400">#{rootIdx + 1}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        title={
                          block.loyaltyMembersOnly
                            ? 'Members only — click to make public'
                            : 'Public — click to restrict to members'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          updateBlockLoyalty(block.id, !block.loyaltyMembersOnly);
                        }}
                        className={cn(
                          'p-1 rounded transition-colors',
                          block.loyaltyMembersOnly
                            ? 'text-amber-500 hover:text-amber-700'
                            : 'text-zinc-300 hover:text-zinc-500',
                        )}
                      >
                        <Lock size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveRootBlock(block.id, 'up');
                        }}
                        disabled={rootIdx === 0}
                        className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 rounded"
                      >
                        <ChevronUp size={13} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveRootBlock(block.id, 'down');
                        }}
                        disabled={rootIdx === rootBlocks.length - 1}
                        className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 rounded"
                      >
                        <ChevronDown size={13} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeBlock(block.id);
                        }}
                        className="p-1 text-zinc-400 hover:text-red-500 rounded"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {isExpanded && def && (
                    <div className="px-4 pb-4 pt-2 border-t border-zinc-100 bg-zinc-50">
                      <BuiltinBlockEditor
                        slug={def.slug}
                        props={block.props}
                        onChange={(props) => updateBlockProps(block.id, props)}
                      />
                      <DynamicBlockEditor
                        def={def}
                        props={block.props}
                        spaceId={spaceId}
                        onChange={(props) => updateBlockProps(block.id, props)}
                      />
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {blocks.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setAddBlockTarget(null);
                  setShowAddBlock(true);
                }}
                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-700 px-2 py-1.5 flex-1 border-2 border-dashed border-zinc-200 rounded-xl justify-center transition-colors hover:border-zinc-300"
              >
                <Plus size={13} /> Add block
              </button>
              <button
                onClick={() => setShowAddRow(true)}
                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-700 px-2 py-1.5 flex-1 border-2 border-dashed border-zinc-200 rounded-xl justify-center transition-colors hover:border-zinc-300"
              >
                <Columns2 size={13} /> Add row
              </button>
            </div>
          )}
        </div>

        {/* Right panel */}
        <div className="space-y-3">
          <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
              Page info
            </p>
            <div className="space-y-1 text-xs text-zinc-600">
              <div className="flex justify-between">
                <span className="text-zinc-400">Status</span>
                <Badge
                  variant={page.status === 'published' ? 'success' : 'default'}
                  className="text-[10px]"
                >
                  {page.status}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Visibility</span>
                <span>{page.isPublic ? 'Public' : 'Private'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Slug</span>
                <span className="font-mono">{page.slug}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Blocks</span>
                <span>{blocks.length}</span>
              </div>
            </div>
            <button
              onClick={() => setEditingMeta(true)}
              className="mt-2 w-full text-xs text-zinc-500 hover:text-zinc-800 border border-zinc-200 rounded-lg py-1.5 transition-colors hover:bg-zinc-50"
            >
              Edit settings
            </button>
          </div>

          {dirty && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
              <Clock size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                You have unsaved changes. Save as draft or publish to keep them.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add Block Modal */}
      <Modal
        open={showAddBlock}
        onClose={() => { setShowAddBlock(false); setAddBlockTarget(null); }}
        title={addBlockTarget ? `Add block to column ${addBlockTarget.colIndex + 1}` : 'Add block'}
      >
        <div className="space-y-4">
          {Object.entries(defsByCategory)
            .map(([category, defs]) => ({ category, defs: defs.filter((d) => d.slug !== 'row') }))
            .filter(({ defs }) => defs.length > 0)
            .map(({ category, defs }) => (
            <div key={category}>
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                {category}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {defs.map((def) => (
                  <button
                    key={def.id}
                    onClick={() => addBlock(def)}
                    className="text-left px-3 py-2.5 rounded-lg border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-zinc-800">{def.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {blockDefs.length === 0 && (
            <p className="text-sm text-zinc-400 text-center py-4">
              No block definitions available. Create some in Block Definitions.
            </p>
          )}
        </div>
      </Modal>

      {/* Add Row Modal */}
      <Modal open={showAddRow} onClose={() => setShowAddRow(false)} title="Add row">
        <div className="space-y-4">
          {([1, 2, 3, 4] as const).map((colCount) => {
            const presets = COLUMN_PRESETS[colCount]!;
            return (
              <div key={colCount}>
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                  {colCount} column{colCount > 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {presets.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        addRow(colCount, preset.widths);
                        setShowAddRow(false);
                      }}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                    >
                      <Columns2 size={14} className="text-zinc-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-zinc-800">{preset.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* Settings Modal */}
      <Modal open={editingMeta} onClose={() => setEditingMeta(false)} title="Page settings">
        <div className="space-y-4">
          <Input
            label="Page title"
            value={metaForm.title}
            onChange={(e) => setMetaForm((f) => ({ ...f, title: e.target.value }))}
          />
          <Input
            label="Meta title"
            value={metaForm.metaTitle}
            onChange={(e) => setMetaForm((f) => ({ ...f, metaTitle: e.target.value }))}
            placeholder="Defaults to page title"
          />
          <Textarea
            label="Meta description"
            rows={3}
            value={metaForm.metaDescription}
            onChange={(e) => setMetaForm((f) => ({ ...f, metaDescription: e.target.value }))}
            placeholder="Short description for search engines"
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={metaForm.isPublic}
              onChange={(e) => setMetaForm((f) => ({ ...f, isPublic: e.target.checked }))}
              className="w-4 h-4 rounded border-zinc-300"
            />
            <span className="text-sm text-zinc-700">Public (visible in customer portal)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={metaForm.loyaltyMembersOnly}
              onChange={(e) => setMetaForm((f) => ({ ...f, loyaltyMembersOnly: e.target.checked }))}
              className="w-4 h-4 rounded border-zinc-300"
            />
            <span className="text-sm text-zinc-700">Loyalty members only</span>
          </label>
          {layouts.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Layout</label>
              <select
                value={metaForm.layoutId}
                onChange={(e) => setMetaForm((f) => ({ ...f, layoutId: e.target.value }))}
                className="w-full border border-zinc-200 rounded-lg text-sm px-3 h-9 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white"
              >
                <option value="">— Space default —</option>
                {layouts.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}{l.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={metaForm.isTemplate}
              onChange={(e) => setMetaForm((f) => ({ ...f, isTemplate: e.target.checked }))}
              className="w-4 h-4 rounded border-zinc-300"
            />
            <div>
              <span className="text-sm text-zinc-700">Template page</span>
              <p className="text-xs text-zinc-400">Template pages are not publicly visible</p>
            </div>
          </label>
        </div>
        {metaMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(metaMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setEditingMeta(false)}>
            Cancel
          </Button>
          <Button
            loading={metaMutation.isPending}
            disabled={!metaForm.title}
            onClick={() =>
              metaMutation.mutate({
                title: metaForm.title,
                metaTitle: metaForm.metaTitle || undefined,
                metaDescription: metaForm.metaDescription || undefined,
                isPublic: metaForm.isPublic,
                loyaltyMembersOnly: metaForm.loyaltyMembersOnly,
                isTemplate: metaForm.isTemplate,
                layoutId: metaForm.layoutId || null,
              })
            }
          >
            Save settings
          </Button>
        </ModalFooter>
      </Modal>

      {/* Versions Modal */}
      <Modal open={showVersions} onClose={() => setShowVersions(false)} title="Page versions">
        {versions.length === 0 ? (
          <p className="text-sm text-zinc-500">No published versions yet.</p>
        ) : (
          <div className="space-y-1">
            {versions.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-zinc-50"
              >
                <div className="flex items-center gap-2">
                  <Clock size={12} className="text-zinc-400" />
                  <span className="text-sm text-zinc-700">{fmtDateTime(v.createdAt)}</span>
                </div>
                <button
                  onClick={() => {
                    setRestoringVersionId(v.id);
                    restoreMutation.mutate(v.id);
                  }}
                  disabled={restoreMutation.isPending && restoringVersionId === v.id}
                  className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1 rounded hover:bg-zinc-100 transition-colors disabled:opacity-50"
                >
                  <RotateCcw size={11} />
                  {restoreMutation.isPending && restoringVersionId === v.id
                    ? 'Restoring…'
                    : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowVersions(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmDialog
        open={false}
        title=""
        message=""
        onConfirm={() => navigate(`/spaces/${spaceId}/pages`)}
        onCancel={() => {}}
      />
    </div>
  );
}
